import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabase';
import { toast } from 'react-hot-toast';
import { FaBookOpen, FaFolder, FaPlus, FaTrash, FaDatabase, FaQuestionCircle, FaHome, FaSignOutAlt, FaFolderPlus } from 'react-icons/fa';
import AddContent from './Admin-Dashboard/AddTopic';
import ManageProducts from './Admin-Dashboard/ManageContent';
import '../styles/dashboard.css';

const AdminDashboard = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  // Statistics State
  const [stats, setStats] = useState({
    topics: 0,
    classes: 0,
    categories: 0,
    questions: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);

  // Management State
  const [classesList, setClassesList] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [newClass, setNewClass] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [addingClass, setAddingClass] = useState(false);
  const [addingCategory, setAddingCategory] = useState(false);

  // Active Menu Section
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch Dashboard Stats and Data
  const fetchDashboardData = async () => {
    setStatsLoading(true);
    try {
      // 1. Get stats counts
      const [topicsCount, classesCount, categoriesCount, questionsCount] = await Promise.all([
        supabase.from('topics').select('*', { count: 'exact', head: true }),
        supabase.from('classes').select('*', { count: 'exact', head: true }),
        supabase.from('categories').select('*', { count: 'exact', head: true }),
        supabase.from('questions').select('*', { count: 'exact', head: true }),
      ]);

      setStats({
        topics: topicsCount.count || 0,
        classes: classesCount.count || 0,
        categories: categoriesCount.count || 0,
        questions: questionsCount.count || 0,
      });

      // 2. Get list of classes & categories
      const [fetchedClasses, fetchedCategories] = await Promise.all([
        supabase.from('classes').select('*').order('name'),
        supabase.from('categories').select('*').order('name'),
      ]);

      setClassesList(fetchedClasses.data || []);
      setCategoriesList(fetchedCategories.data || []);
    } catch (error) {
      console.error('Error fetching dashboard statistics:', error);
      toast.error('Failed to load dashboard metrics.');
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Handle Logout
  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully.');
      navigate('/login');
    } catch (err) {
      toast.error('Logout failed.');
    }
  };

  // Add a Class
  const handleAddClass = async (e) => {
    e.preventDefault();
    if (!newClass.trim()) return;

    if (classesList.some(c => c.name.toLowerCase() === newClass.trim().toLowerCase())) {
      toast.error('Class already exists.');
      return;
    }

    setAddingClass(true);
    try {
      const { data, error } = await supabase
        .from('classes')
        .insert([{ name: newClass.trim() }])
        .select();

      if (error) throw error;
      
      toast.success(`Class "${newClass}" added.`);
      setNewClass('');
      if (data) {
        setClassesList(prev => [...prev, ...data].sort((a,b) => a.name.localeCompare(b.name)));
        setStats(prev => ({ ...prev, classes: prev.classes + 1 }));
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to add class.');
    } finally {
      setAddingClass(false);
    }
  };

  // Delete a Class
  const handleDeleteClass = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete Class "${name}"?`)) return;

    try {
      const { error } = await supabase.from('classes').delete().eq('id', id);
      if (error) throw error;

      toast.success(`Class "${name}" deleted.`);
      setClassesList(prev => prev.filter(c => c.id !== id));
      setStats(prev => ({ ...prev, classes: Math.max(0, prev.classes - 1) }));
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete class.');
    }
  };

  // Add a Category (Subject Category)
  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) return;

    if (categoriesList.some(c => c.name.toLowerCase() === newCategory.trim().toLowerCase())) {
      toast.error('Category already exists.');
      return;
    }

    setAddingCategory(true);
    try {
      const { data, error } = await supabase
        .from('categories')
        .insert([{ name: newCategory.trim() }])
        .select();

      if (error) throw error;

      toast.success(`Category "${newCategory}" added.`);
      setNewCategory('');
      if (data) {
        setCategoriesList(prev => [...prev, ...data].sort((a,b) => a.name.localeCompare(b.name)));
        setStats(prev => ({ ...prev, categories: prev.categories + 1 }));
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to add category.');
    } finally {
      setAddingCategory(false);
    }
  };

  // Delete a Category
  const handleDeleteCategory = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete Category "${name}"?`)) return;

    try {
      const { error } = await supabase.from('categories').delete().eq('id', id);
      if (error) throw error;

      toast.success(`Category "${name}" deleted.`);
      setCategoriesList(prev => prev.filter(c => c.id !== id));
      setStats(prev => ({ ...prev, categories: Math.max(0, prev.categories - 1) }));
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete category.');
    }
  };

  return (
    <div className="dashboard-wrapper-full" style={styles.wrapper}>
      <div style={styles.container}>
        
        {/* Horizontal Navigation Menu */}
        <div style={styles.navBar}>
          <div style={styles.logo}>Gramture Admin</div>
          <div style={styles.navLinks}>
            <button 
              style={{ ...styles.navLink, ...(activeTab === 'overview' ? styles.activeNavLink : {}) }}
              onClick={() => setActiveTab('overview')}
            >
              <FaDatabase /> Overview
            </button>
            
            <button 
              style={{ ...styles.navLink, ...(activeTab === 'manage' ? styles.activeNavLink : {}) }}
              onClick={() => setActiveTab('manage')}
            >
              <FaFolderPlus /> Manage Sections
            </button>

            <button 
              style={{ ...styles.navLink, ...(activeTab === 'add' ? styles.activeNavLink : {}) }}
              onClick={() => setActiveTab('add')}
            >
              <FaPlus /> Upload Notes/Papers
            </button>

            <button 
              style={{ ...styles.navLink, ...(activeTab === 'library' ? styles.activeNavLink : {}) }}
              onClick={() => setActiveTab('library')}
            >
              <FaBookOpen /> Manage Content Items
            </button>

            <Link to="/" style={styles.navLink}>
              <FaHome /> Back to Site
            </Link>

            <button onClick={handleLogout} style={styles.logoutBtn}>
              <FaSignOutAlt /> Log Out
            </button>
          </div>
        </div>

        {/* Main Dashboard Panel */}
        <main style={{ padding: '0px 0 100px 0' }}>
          {activeTab === 'overview' && (
            <div className="dashboard-overview-container">
              <div className="dashboard-welcome-header">
                <h1 className="dashboard-section-title">Welcome Back, Admin!</h1>
                <p className="dashboard-subtitle">Here is what's happening with your platform today.</p>
              </div>

              {statsLoading ? (
                <div className="loading-state">
                  <div className="spinner-border text-primary" role="status"></div>
                  <p>Loading database aggregates...</p>
                </div>
              ) : (
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-icon stat-icon-blue">
                      <FaBookOpen />
                    </div>
                    <div className="stat-info">
                      <span className="stat-value">{stats.topics}</span>
                      <span className="stat-label">Total Topics</span>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon stat-icon-indigo">
                      <FaHome />
                    </div>
                    <div className="stat-info">
                      <span className="stat-value">{stats.classes}</span>
                      <span className="stat-label">Active Classes</span>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon stat-icon-cyan">
                      <FaFolder />
                    </div>
                    <div className="stat-info">
                      <span className="stat-value">{stats.categories}</span>
                      <span className="stat-label">Subject Categories</span>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon stat-icon-red">
                      <FaQuestionCircle />
                    </div>
                    <div className="stat-info">
                      <span className="stat-value">{stats.questions}</span>
                      <span className="stat-label">Forum Questions</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="quick-actions-panel">
                <div className="qa-header">
                  <h3>Quick Actions</h3>
                  <p>Manage your platform efficiently</p>
                </div>
                <div className="qa-grid">
                  <button onClick={() => setActiveTab('add')} className="qa-card qa-primary">
                    <div className="qa-icon"><FaPlus /></div>
                    <div className="qa-content">
                      <h4>Add New Content</h4>
                      <span>Upload notes, papers & materials</span>
                    </div>
                  </button>
                  <button onClick={() => setActiveTab('library')} className="qa-card qa-secondary">
                    <div className="qa-icon"><FaBookOpen /></div>
                    <div className="qa-content">
                      <h4>Manage Library</h4>
                      <span>Edit existing topics & resources</span>
                    </div>
                  </button>
                  <button onClick={() => setActiveTab('manage')} className="qa-card qa-tertiary">
                    <div className="qa-icon"><FaFolderPlus /></div>
                    <div className="qa-content">
                      <h4>Manage Sections</h4>
                      <span>Organize classes & categories</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'manage' && (
            <div>
              <h1 className="dashboard-section-title">Manage Classes & Categories</h1>
              
              <div className="mgmt-grid">
                {/* Classes Manager */}
                <div className="mgmt-card">
                  <h3>Manage Educational Classes</h3>
                  <form onSubmit={handleAddClass} className="mgmt-form">
                    <input
                      type="text"
                      placeholder="e.g. Class 9, Class 10"
                      className="glass-input"
                      value={newClass}
                      onChange={(e) => setNewClass(e.target.value)}
                      disabled={addingClass}
                    />
                    <button 
                      type="submit" 
                      className="btn-premium btn-premium-primary"
                      disabled={addingClass || !newClass.trim()}
                    >
                      <FaPlus size={12} /> {addingClass ? 'Adding...' : 'Add Class'}
                    </button>
                  </form>

                  <ul className="mgmt-list">
                    {classesList.length === 0 ? (
                      <li className="no-topics">No classes found</li>
                    ) : (
                      classesList.map(c => (
                        <li key={c.id} className="mgmt-list-item">
                          <span>{c.name}</span>
                          <button 
                            onClick={() => handleDeleteClass(c.id, c.name)} 
                            className="btn-premium btn-premium-danger"
                            style={{ padding: '4px 8px', borderRadius: '4px' }}
                          >
                            <FaTrash size={12} />
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                </div>

                {/* Categories Manager */}
                <div className="mgmt-card">
                  <h3>Manage Subject Categories</h3>
                  <form onSubmit={handleAddCategory} className="mgmt-form">
                    <input
                      type="text"
                      placeholder="e.g. English, Physics"
                      className="glass-input"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      disabled={addingCategory}
                    />
                    <button 
                      type="submit" 
                      className="btn-premium btn-premium-primary"
                      disabled={addingCategory || !newCategory.trim()}
                    >
                      <FaPlus size={12} /> {addingCategory ? 'Adding...' : 'Add Category'}
                    </button>
                  </form>

                  <ul className="mgmt-list">
                    {categoriesList.length === 0 ? (
                      <li className="no-topics">No categories found</li>
                    ) : (
                      categoriesList.map(c => (
                        <li key={c.id} className="mgmt-list-item">
                          <span>{c.name}</span>
                          <button 
                            onClick={() => handleDeleteCategory(c.id, c.name)} 
                            className="btn-premium btn-premium-danger"
                            style={{ padding: '4px 8px', borderRadius: '4px' }}
                          >
                            <FaTrash size={12} />
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'add' && (
            <div>
              <h1 className="dashboard-section-title">Upload Notes/Papers</h1>
              <AddContent />
            </div>
          )}

          {activeTab === 'library' && (
            <div>
              <h1 className="dashboard-section-title">Manage Content Items</h1>
              <ManageProducts />
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

const styles = {
  wrapper: {
    minHeight: '100vh',
    background: 'transparent',
    paddingTop: '20px',
    fontFamily: "'Outfit', 'Inter', sans-serif"
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 24px',
    boxSizing: 'border-box'
  },
  navBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'transparent',
    border: 'none',
    padding: '8px 0px',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '16px',
    boxShadow: 'none',
  },
  logo: {
    fontSize: '18px',
    fontWeight: '800',
    color: '#1e293b',
    letterSpacing: '-0.3px',
  },
  navLinks: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  navLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px',
    background: 'transparent',
    border: 'none',
    color: '#64748b',
    fontSize: '14px',
    fontWeight: '800',
    borderRadius: '0px',
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'all 0.2s',
  },
  activeNavLink: {
    color: '#4f46e5',
    borderBottom: '2px solid #4f46e5',
  },
  logoutBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px',
    background: 'transparent',
    border: 'none',
    color: '#ef4444',
    fontSize: '14px',
    fontWeight: '800',
    cursor: 'pointer',
    transition: 'all 0.2s',
  }
};

export default AdminDashboard;
